'use client';

import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';

export interface TransferData {
  // Step 1: Amounts
  fromAmount: string;
  toAmount: string;
  fromCurrency: string;
  toCurrency: string;
  exchangeRate: number;
  associatedCost: number;
  totalAmount: number;
  
  // Step 2: Destination
  beneficiaryId: string;
  transferReasonId: string;
  concept: string;
  
  // Step 3: Summary
  digitalUsdConfirmation: boolean;
  
  // Step 4: Payment
  bankAccountId: string;
  
  // Metadata
  operationType: 'USA-COL' | 'COL-USA';
  fromCountry: string;
  toCountry: string;
  status: 'draft' | 'pending' | 'completed' | 'cancelled';
  expiresAt: string;
}

interface TransferContextType {
  transferData: TransferData;
  updateTransferData: (data: Partial<TransferData>) => void;
  resetTransferData: () => void;
  isStepComplete: (step: number) => boolean;
  canProceedToStep: (step: number) => boolean;
}

const defaultTransferData: TransferData = {
  fromAmount: '',
  toAmount: '',
  fromCurrency: 'USD',
  toCurrency: 'COP',
  exchangeRate: 0,
  associatedCost: 0,
  totalAmount: 0,
  beneficiaryId: '',
  transferReasonId: '',
  concept: '',
  digitalUsdConfirmation: false,
  bankAccountId: '',
  operationType: 'USA-COL',
  fromCountry: 'USA',
  toCountry: 'COL',
  status: 'draft',
  expiresAt: '',
};

const TransferContext = createContext<TransferContextType | undefined>(undefined);

export const useTransfer = () => {
  const context = useContext(TransferContext);
  if (context === undefined) {
    throw new Error('useTransfer must be used within a TransferProvider');
  }
  return context;
};

interface TransferProviderProps {
  children: ReactNode;
}

export const TransferProvider: React.FC<TransferProviderProps> = ({ children }) => {
  const [transferData, setTransferData] = useState<TransferData>(defaultTransferData);

  // Load data from localStorage on mount
  useEffect(() => {
    const savedData = localStorage.getItem('zulu-transfer-data');
    if (savedData) {
      try {
        const parsedData = JSON.parse(savedData);
        setTransferData({ ...defaultTransferData, ...parsedData });
      } catch (error) {
        console.error('Error loading transfer data from localStorage:', error);
      }
    }
  }, []);

  // Save data to localStorage whenever it changes
  useEffect(() => {
    localStorage.setItem('zulu-transfer-data', JSON.stringify(transferData));
  }, [transferData]);

  const updateTransferData = (data: Partial<TransferData>) => {
    setTransferData(prev => ({ ...prev, ...data }));
  };

  const resetTransferData = () => {
    setTransferData(defaultTransferData);
    localStorage.removeItem('zulu-transfer-data');
  };

  const isStepComplete = (step: number): boolean => {
    switch (step) {
      case 1: // Amounts
        return !!(transferData.fromAmount && transferData.toAmount && transferData.exchangeRate > 0);
      case 2: // Destination
        return !!(transferData.beneficiaryId && transferData.transferReasonId && transferData.concept);
      case 3: // Summary
        return transferData.digitalUsdConfirmation;
      case 4: // Payment
        return !!transferData.bankAccountId;
      default:
        return false;
    }
  };

  const canProceedToStep = (step: number): boolean => {
    switch (step) {
      case 1:
        return true; // Can always start
      case 2:
        return isStepComplete(1);
      case 3:
        return isStepComplete(1) && isStepComplete(2);
      case 4:
        return isStepComplete(1) && isStepComplete(2) && isStepComplete(3);
      default:
        return false;
    }
  };

  const value: TransferContextType = {
    transferData,
    updateTransferData,
    resetTransferData,
    isStepComplete,
    canProceedToStep,
  };

  return (
    <TransferContext.Provider value={value}>
      {children}
    </TransferContext.Provider>
  );
};
